import { useState, useEffect, useRef } from 'react';
import { useAuth, UserRole } from '../contexts/AuthContext';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { FULL_NAME_FORMAT_EXAMPLE, FULL_NAME_FORMAT_HINT, normalizeStudentId, validateStudentFullNameFormat } from '../utils/studentRegistry';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { 
  GraduationCap, 
  Eye, 
  EyeOff, 
  Mail, 
  Lock, 
  User, 
  Building2,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  ScrollText,
  Clock,
  Shield,
  Sparkles,
  Monitor,
} from 'lucide-react';

interface AuthPageProps {
  onBackToLanding?: () => void;
}

export function AuthPage({ onBackToLanding }: AuthPageProps = {}) {
  const { login, signup } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  
  // Browser history integration for auth form navigation
  const authPageSessionRef = useRef<number | null>(null);
  const suppressNextAuthPopstateRef = useRef(false);
  
  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  
  // Signup state
  const [signupData, setSignupData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    role: 'student' as UserRole,
    studentId: '',
    forgotStudentId: false
  });
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const signupNameValidation = signupData.role === 'student'
    ? validateStudentFullNameFormat(signupData.name)
    : { isValid: true, message: '', formattedValue: signupData.name };

  // Real-time stats
  const [stats, setStats] = useState({ totalQueues: '0', serviceWindows: '6', avgWaitTime: '—', completionRate: '100%' });

  useEffect(() => {
    const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-d4ddf86c`;
    const fetchStats = async () => {
      try {
        const [queuesRes, diagnosticsRes] = await Promise.all([
          fetch(`${API_BASE}/queues`, {
            headers: { Authorization: `Bearer ${publicAnonKey}` },
          }),
          fetch(`${API_BASE}/diagnostics`, {
            headers: { Authorization: `Bearer ${publicAnonKey}` },
          }),
        ]);
        if (!queuesRes.ok || !diagnosticsRes.ok) return;

        const { queues } = await queuesRes.json();
        const diagnosticsJson = await diagnosticsRes.json();
        if (!Array.isArray(queues)) return;

        const activeWindows =
          diagnosticsJson?.system?.activeWindows ??
          (diagnosticsJson?.windows || []).filter((w: { isActive?: boolean }) => w?.isActive).length ?? 0;

        let completedCount = 0, totalWait = 0, withTimes = 0;
        for (const q of queues) {
          if (q.status === 'completed') {
            completedCount++;
            // Average serving time: counts only active serving time (paused during on_hold)
            if (q.servedAt && q.completedAt) {
              const servingTime = new Date(q.completedAt).getTime() - new Date(q.servedAt).getTime() - (q.totalHoldDuration || 0);
              if (servingTime > 0) { totalWait += servingTime; withTimes++; }
            }
          }
        }

        let avgWait = '—';
        if (withTimes > 0) {
          const avgMin = Math.round(totalWait / withTimes / 60000);
          avgWait = avgMin > 0 ? `~${avgMin} min` : '<1 min';
        }

        const totalQueues = queues.length;
        const completionRate = totalQueues > 0
          ? Math.round((completedCount / totalQueues) * 100)
          : 0;

        setStats({
          totalQueues: queues.length > 0 ? queues.length.toLocaleString() : '0',
          serviceWindows: activeWindows > 0 ? activeWindows.toString() : '6',
          avgWaitTime: avgWait,
          completionRate: `${completionRate}%`,
        });
      } catch { /* silent */ }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  // Calculate password strength
  const calculatePasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 10;
    return Math.min(strength, 100);
  };

  const handlePasswordChange = (password: string) => {
    setSignupData({ ...signupData, password });
    setPasswordStrength(calculatePasswordStrength(password));
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!loginEmail.trim() || !loginPassword.trim()) {
      setError('Email Address and Password are required.');
      return;
    }

    setLoading(true);

    try {
      const result = await login(loginEmail, loginPassword);
      if (!result.success) {
        setError(result.message || 'Invalid email or password. Please try again.');
      } else {
        // Clear auth history session on successful login
        authPageSessionRef.current = null;
      }
    } catch {
      setError('An error occurred during login. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (signupData.password !== signupData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (signupData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (signupData.role === 'student' && !signupData.forgotStudentId && !signupData.studentId) {
      setError('Student ID is required');
      return;
    }

    if (signupData.role === 'student' && !signupNameValidation.isValid) {
      setError(signupNameValidation.message);
      return;
    }

    setLoading(true);

    try {
      const additionalInfo =
        signupData.role === 'student' && !signupData.forgotStudentId
          ? { studentId: normalizeStudentId(signupData.studentId) }
          : undefined;

      const result = await signup(
        signupData.email,
        signupData.password,
        signupData.role === 'student' ? signupNameValidation.formattedValue : signupData.name,
        signupData.role,
        additionalInfo
      );

      if (!result.success) {
        setError(result.message || 'Failed to create account. Email may already be in use.');
        return;
      }

      if (result.requiresApproval) {
        setSuccessMessage(result.message || 'Registration submitted. Wait for admin verification before signing in.');
        setSignupData({
          email: '',
          password: '',
          confirmPassword: '',
          name: '',
          role: 'student',
          studentId: '',
          forgotStudentId: false,
        });
        setIsLogin(true);
        // Clear auth history session on successful signup
        authPageSessionRef.current = null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during signup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setSuccessMessage('');
    
    // Push history entry when toggling between login and signup
    if (authPageSessionRef.current === null) {
      authPageSessionRef.current = Date.now();
      window.history.pushState(
        {
          ...window.history.state,
          authPageSession: authPageSessionRef.current,
          authFormMode: !isLogin ? 'signup' : 'login',
        },
        ''
      );
    } else {
      window.history.pushState(
        {
          ...window.history.state,
          authPageSession: authPageSessionRef.current,
          authFormMode: !isLogin ? 'signup' : 'login',
        },
        ''
      );
    }
  };
  
  // Set up history tracking and popstate handler
  useEffect(() => {
    // Initialize session when component mounts
    authPageSessionRef.current = Date.now();
    window.history.pushState(
      {
        ...window.history.state,
        authPageSession: authPageSessionRef.current,
        authFormMode: isLogin ? 'login' : 'signup',
      },
      ''
    );

    const handleBrowserBackForAuth = () => {
      if (suppressNextAuthPopstateRef.current) {
        suppressNextAuthPopstateRef.current = false;
        return;
      }

      // Check if we're still on auth page and have history
      if (window.history.state?.authPageSession === authPageSessionRef.current) {
        // Toggle between login and signup
        setIsLogin((prev) => !prev);
        setError('');
        setSuccessMessage('');
        return;
      }

      // Otherwise, go back to landing page
      if (onBackToLanding) {
        onBackToLanding();
      }
    };

    window.addEventListener('popstate', handleBrowserBackForAuth);
    return () => {
      window.removeEventListener('popstate', handleBrowserBackForAuth);
    };
  }, [onBackToLanding]);

  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case 'student':
        return <GraduationCap className="h-5 w-5" />;
      case 'staff':
        return <User className="h-5 w-5" />;
      case 'encoder':
        return <ScrollText className="h-5 w-5" />;
      case 'administrator':
        return <Building2 className="h-5 w-5" />;
      default:
        return <User className="h-5 w-5" />;
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 40) return 'bg-red-500';
    if (passwordStrength < 70) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 40) return 'Weak';
    if (passwordStrength < 70) return 'Medium';
    return 'Strong';
  };

  /* ---------- Features shown on the branding panel ---------- */
  const features = [
    { icon: Clock, text: 'Real-time queue tracking' },
    { icon: Monitor, text: 'Live queue monitor displays' },
    { icon: Sparkles, text: 'AI-powered assistance' },
    { icon: Shield, text: 'Secure academic records' },
  ];

  return (
    <div className="min-h-screen flex relative overflow-hidden bg-white">
      {/* Inline animations */}
      <style>{`
        @keyframes auth-pulse{0%,100%{opacity:.35;transform:scale(1)}50%{opacity:.55;transform:scale(1.12)}}
        @keyframes auth-fadeInUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes auth-slideInLeft{from{opacity:0;transform:translateX(-16px)}to{opacity:1;transform:translateX(0)}}
        @keyframes auth-slideInRight{from{opacity:0;transform:translateX(16px)}to{opacity:1;transform:translateX(0)}}
        .auth-pulse{animation:auth-pulse 8s ease-in-out infinite}
        .auth-fadeInUp{animation:auth-fadeInUp .5s ease-out both}
        .auth-slideInLeft{animation:auth-slideInLeft .35s ease-out both}
        .auth-slideInRight{animation:auth-slideInRight .35s ease-out both}
        .pw-bar{transition:width .3s ease-out}
      `}</style>

      {/* ═══════ LEFT BRANDING PANEL (desktop only) ═══════ */}
      <div className="hidden md:flex md:w-[40%] lg:w-[48%] relative bg-gradient-to-br from-blue-700 via-indigo-700 to-violet-800 text-white flex-col justify-between p-8 lg:p-10 xl:p-14 overflow-hidden">
        {/* Decorative blobs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-28 -left-28 w-80 h-80 bg-white/10 rounded-full blur-3xl auth-pulse" />
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-white/5 rounded-full blur-3xl auth-pulse" style={{ animationDelay: '3s' }} />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle,#fff 1px,transparent 1px)', backgroundSize: '22px 22px' }} />
        </div>

        {/* Top — Logo */}
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-10">
            <img src="/regiqueueai.png" alt="NEMSU Logo" className="h-14 w-14 rounded-2xl shadow-lg border-2 border-white/20" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Serve Smart</h1>
              <p className="text-blue-200 text-sm">NEMSU Registrar Queue System</p>
            </div>
          </div>
          <h2 className="text-3xl xl:text-4xl font-extrabold leading-tight mb-4 max-w-md">Your Registrar Experience, Simplified</h2>
          <p className="text-blue-100 leading-relaxed max-w-md">Join the queue from anywhere, track your place in real time, and get served faster.</p>
        </div>

        {/* Middle — Feature list */}
        <div className="relative z-10 space-y-5 my-8">
          {features.map((f, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0 border border-white/10">
                <f.icon className="h-5 w-5 text-blue-200" />
              </div>
              <span className="text-blue-50 text-sm font-medium">{f.text}</span>
            </div>
          ))}
        </div>

        {/* Bottom — Stats */}
        <div className="relative z-10">
          <div className="flex gap-8 text-sm">
            <div><p className="text-2xl font-bold">{stats.totalQueues}</p><p className="text-blue-200 text-xs">Queues Served</p></div>
            <div><p className="text-2xl font-bold">{stats.serviceWindows}</p><p className="text-blue-200 text-xs">Service Windows</p></div>
            <div><p className="text-2xl font-bold">{stats.avgWaitTime}</p><p className="text-blue-200 text-xs">Avg. Wait Time</p></div>
            <div><p className="text-2xl font-bold">{stats.completionRate}</p><p className="text-blue-200 text-xs">Completion Rate</p></div>
          </div>
          <p className="text-blue-300/60 text-xs mt-6">&copy; {new Date().getFullYear()} NEMSU — Serve Smart</p>
        </div>
      </div>

      {/* ═══════ RIGHT FORM PANEL ═══════ */}
      <div className="flex-1 flex flex-col">
        {/* Mobile background blobs */}
        <div className="absolute inset-0 md:hidden pointer-events-none overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400/15 rounded-full blur-3xl auth-pulse" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-400/10 rounded-full blur-3xl auth-pulse" style={{ animationDelay: '3s' }} />
        </div>

        {/* Mobile header */}
        <div className="md:hidden flex items-center gap-3 px-5 pt-5 pb-2 relative z-10">
          {onBackToLanding && (
            <button onClick={onBackToLanding} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group mr-auto">
              <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" /> Home
            </button>
          )}
          <img src="/regiqueueai.png" alt="Logo" className="h-9 w-9 rounded-xl shadow ml-auto" />
        </div>

        {/* Centered form area */}
        <div className="flex-1 flex items-center justify-center px-5 py-8 relative z-10">
          <div className="w-full max-w-[420px] auth-fadeInUp">
            {/* Desktop back link */}
            {onBackToLanding && (
              <button onClick={onBackToLanding} className="hidden md:flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-5 transition-colors group">
                <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" /> Back to Home
              </button>
            )}

            {/* Mobile branding */}
            <div className="md:hidden text-center mb-6">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-600 bg-clip-text text-transparent">Serve Smart</h1>
              <p className="text-muted-foreground text-sm mt-1">NEMSU Registrar Queue System</p>
            </div>

            {/* Desktop heading */}
            <div className="hidden md:block mb-7">
              <h2 className="text-2xl font-bold text-foreground">{isLogin ? 'Welcome back' : 'Create your account'}</h2>
              <p className="text-muted-foreground text-sm mt-1">{isLogin ? 'Sign in to access the NEMSU registrar queue system' : 'Get started with the NEMSU registrar services'}</p>
            </div>

            <Card className="shadow-xl border-0 bg-white/95 backdrop-blur-sm md:shadow-lg md:border md:border-border/50">
              <CardContent className="p-6">
                {/* Tabs */}
                <div className="bg-muted rounded-xl p-1 mb-6">
                  <div className="grid grid-cols-2 gap-1">
                    <button type="button" onClick={() => !isLogin && toggleMode()} className={`py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${isLogin ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                      Sign In
                    </button>
                    <button type="button" onClick={() => isLogin && toggleMode()} className={`py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${!isLogin ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                      Sign Up
                    </button>
                  </div>
                </div>

                {isLogin ? (
                  <form key="login" onSubmit={handleLoginSubmit} className="space-y-4 auth-slideInLeft">
                    {successMessage && (
                      <Alert className="border-emerald-200 bg-emerald-50 text-emerald-900">
                        <AlertDescription className="text-sm">{successMessage}</AlertDescription>
                      </Alert>
                    )}

                    {error && (
                      <Alert variant="destructive" className="border-red-200 bg-red-50">
                        <AlertDescription className="text-sm">{error}</AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-1.5">
                      <Label htmlFor="login-email" className="text-xs font-medium flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground" /> Email Address
                      </Label>
                      <Input id="login-email" type="email" placeholder="your.email@nemsu.edu.ph" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required className="h-11 bg-muted/40 border-border/60 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all" />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="login-password" className="text-xs font-medium flex items-center gap-1.5">
                        <Lock className="h-3.5 w-3.5 text-muted-foreground" /> Password
                      </Label>
                      <div className="relative">
                        <Input id="login-password" type={showLoginPassword ? 'text' : 'password'} placeholder="Enter your password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required className="h-11 pr-10 bg-muted/40 border-border/60 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all" />
                        <button type="button" onClick={() => setShowLoginPassword(!showLoginPassword)} className="absolute inset-y-0 right-2.5 flex items-center text-muted-foreground hover:text-foreground transition-colors">
                          {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <Button type="submit" className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 transition-all active:scale-[0.98]" loading={loading} loadingText="Signing in...">
                      <span className="flex items-center gap-2">Sign In <ArrowRight className="h-4 w-4" /></span>
                    </Button>

                  </form>
                ) : (
                  <form key="signup" onSubmit={handleSignupSubmit} className="space-y-4 auth-slideInRight">
                    {error && (
                      <Alert variant="destructive" className="border-red-200 bg-red-50">
                        <AlertDescription className="text-sm">{error}</AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-1.5">
                      <Label htmlFor="signup-name" className="text-xs font-medium flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5 text-muted-foreground" /> Full Name
                        <span className="ml-1 text-red-600 font-semibold leading-none" aria-label="required">*</span>
                      </Label>
                      <Input id="signup-name" type="text" placeholder={FULL_NAME_FORMAT_EXAMPLE} value={signupData.name} onChange={(e) => setSignupData({ ...signupData, name: e.target.value })} required className="h-11 bg-muted/40 border-border/60 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all" />
                      {signupData.role === 'student' && (
                        <>
                          <p className={`text-[11px] ${signupData.name && !signupNameValidation.isValid ? 'text-red-500' : 'text-muted-foreground'}`}>
                            Format: {FULL_NAME_FORMAT_HINT}. Example: {FULL_NAME_FORMAT_EXAMPLE}
                          </p>
                          {signupData.name && signupNameValidation.isValid && (
                            <p className="text-[11px] text-emerald-600">
                              Your full name will be normalized and checked against the Student Data Bank. If no matching record exists, your registration can still be submitted for admin verification.
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="signup-email" className="text-xs font-medium flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground" /> Email Address
                        <span className="ml-1 text-red-600 font-semibold leading-none" aria-label="required">*</span>
                      </Label>
                      <Input id="signup-email" type="email" placeholder="your.email@nemsu.edu.ph" value={signupData.email} onChange={(e) => setSignupData({ ...signupData, email: e.target.value })} required className="h-11 bg-muted/40 border-border/60 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all" />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="signup-role" className="text-xs font-medium flex items-center gap-1.5">
                        {getRoleIcon(signupData.role)} Role
                      </Label>
                      <Select value={signupData.role} onValueChange={(value: UserRole) => setSignupData({ ...signupData, role: value })}>
                        <SelectTrigger className="h-11 bg-muted/40 border-border/60 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all">
                          <SelectValue placeholder="Select your role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="student">
                            <div className="flex items-center gap-2"><GraduationCap className="h-4 w-4" /> Student</div>
                          </SelectItem>
                          {/* 
                          <SelectItem value="staff">
                            <div className="flex items-center gap-2"><User className="h-4 w-4" /> Staff (Window-in-charge)</div>
                          </SelectItem>
                          <SelectItem value="encoder">
                            <div className="flex items-center gap-2"><ScrollText className="h-4 w-4" /> Encoder (Document Processing)</div>
                          </SelectItem>
                          <SelectItem value="administrator">
                            <div className="flex items-center gap-2"><Building2 className="h-4 w-4" /> Administrator</div>
                          </SelectItem>
                          */}
                        </SelectContent>
                      </Select>
                    </div>

                    {signupData.role === 'student' && (
                      <div className="space-y-1.5">
                        <Label htmlFor="signup-studentId" className="text-xs font-medium flex items-center gap-1.5">
                          <GraduationCap className="h-3.5 w-3.5 text-muted-foreground" /> Student ID
                          {!signupData.forgotStudentId && (
                            <span className="ml-1 text-red-600 font-semibold leading-none" aria-label="required">*</span>
                          )}
                        </Label>
                        <Input id="signup-studentId" type="text" placeholder="2024-00001" value={signupData.studentId} onChange={(e) => setSignupData({ ...signupData, studentId: e.target.value.toUpperCase() })} required={!signupData.forgotStudentId} disabled={signupData.forgotStudentId} className="h-11 bg-muted/40 border-border/60 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all disabled:cursor-not-allowed disabled:opacity-70" />
                        <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/20 p-3">
                          <Checkbox
                            id="signup-forgot-student-id"
                            checked={signupData.forgotStudentId}
                            onCheckedChange={(checked) =>
                              setSignupData({
                                ...signupData,
                                forgotStudentId: checked === true,
                                studentId: checked === true ? '' : signupData.studentId,
                              })
                            }
                          />
                          <div className="space-y-1">
                            <Label htmlFor="signup-forgot-student-id" className="text-xs font-medium leading-none">
                              I forgot my ID Number
                            </Label>
                            <p className="text-[11px] text-muted-foreground">
                              Check this to continue signup even if you do not remember your ID Number.
                            </p>
                          </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          {signupData.forgotStudentId
                            ? 'ID Number entry is optional. If your full name is not in the Student Data Bank, your registration will be queued for admin verification.'
                            : 'Primary validation key. If the Student ID is not yet in the Student Data Bank, registration can still be submitted but must be approved by an administrator before sign-in.'}
                        </p>
                      </div>
                    )}

                    <div className="space-y-1.5">
                      <Label htmlFor="signup-password" className="text-xs font-medium flex items-center gap-1.5">
                        <Lock className="h-3.5 w-3.5 text-muted-foreground" /> Password
                        <span className="ml-1 text-red-600 font-semibold leading-none" aria-label="required">*</span>
                      </Label>
                      <div className="relative">
                        <Input id="signup-password" type={showSignupPassword ? 'text' : 'password'} placeholder="Create a strong password" value={signupData.password} onChange={(e) => handlePasswordChange(e.target.value)} required className="h-11 pr-10 bg-muted/40 border-border/60 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all" />
                        <button type="button" onClick={() => setShowSignupPassword(!showSignupPassword)} className="absolute inset-y-0 right-2.5 flex items-center text-muted-foreground hover:text-foreground transition-colors">
                          {showSignupPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {signupData.password && (
                        <div className="flex items-center gap-2 pt-1">
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className={`h-full pw-bar rounded-full ${getPasswordStrengthColor()}`} style={{ width: `${passwordStrength}%` }} />
                          </div>
                          <span className="text-[11px] font-medium text-muted-foreground min-w-[52px] text-right">{getPasswordStrengthText()}</span>
                        </div>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="signup-confirmPassword" className="text-xs font-medium flex items-center gap-1.5">
                        <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" /> Confirm Password
                        <span className="ml-1 text-red-600 font-semibold leading-none" aria-label="required">*</span>
                      </Label>
                      <div className="relative">
                        <Input id="signup-confirmPassword" type={showConfirmPassword ? 'text' : 'password'} placeholder="Re-enter your password" value={signupData.confirmPassword} onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })} required className="h-11 pr-10 bg-muted/40 border-border/60 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all" />
                        <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-2.5 flex items-center text-muted-foreground hover:text-foreground transition-colors">
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {signupData.password && signupData.confirmPassword && signupData.password !== signupData.confirmPassword && (
                        <p className="text-xs text-red-500">Passwords do not match</p>
                      )}
                      {signupData.password && signupData.confirmPassword && signupData.password === signupData.confirmPassword && (
                        <p className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Passwords match</p>
                      )}
                    </div>

                    <Button type="submit" className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 transition-all active:scale-[0.98]" loading={loading} loadingText="Creating account...">
                      <span className="flex items-center gap-2">Create Account <ArrowRight className="h-4 w-4" /></span>
                    </Button>

                    <p className="text-[11px] font-semibold text-red-700">* Required field</p>
                  </form>
                )}
              </CardContent>
            </Card>

            <p className="text-center text-xs text-muted-foreground mt-5 auth-fadeInUp" style={{ animationDelay: '.35s' }}>
              By continuing, you agree to the Serve Smart Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
